import { useCallback, useEffect } from 'react';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { get, isEmpty, toInteger } from 'lodash';

import { FETCHING_STATUSES } from 'constants/globals';
import thematize from 'lib/thematize';
import { scrollTo } from 'lib/utils';
import { useCustomRouterLocation, useDispatch, useNavigate, useSelector } from 'hooks';
import { getCompanies } from 'slices/adminPanel';
import NewCompany from 'components/AdminPanel/Company/NewCompany';
import Empty from 'components/AdminPanel/Misc/Empty';
import ContentLayout from 'components/Base/ContentLayout';
import Spinner from 'components/Base/Spinner';
import Pagination from 'components/PaginationTailless';
import { offsetFormatToPageFormat } from 'components/PaginationTailless/lib';
import Reload from 'components/ReloadButton';
import Actions from './Actions';
import Filters from './Filters';
import messages from './messages';
import Table from './Table';
import styles from './Companies.scss';

const theme = thematize(styles);

const Companies = (): React.ReactElement => {
  const navigate = useNavigate();
  const location = useCustomRouterLocation();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const companies = useSelector(({ adminPanel }) => adminPanel.companies);
  const pages = offsetFormatToPageFormat(companies.pages || ({} as API$Pages));
  const isForm = location.pathname.includes('new');

  const handleGetCompanies = useCallback(() => {
    dispatch(getCompanies(location.query));
  }, [location.query, dispatch]);

  useEffect(() => {
    handleGetCompanies();
  }, [handleGetCompanies]);

  const handlePageChange = (page: number): void => {
    const defaultLimit = 50;
    const { limit } = location.query;
    const offset = (page - 1) * (toInteger(limit) || defaultLimit);
    const newLocation = {
      pathname: location.pathname,
      query: {
        ...location.query,
        offset,
      },
    };

    scrollTo(0);
    navigate(newLocation);
  };

  const renderTable = (): React.ReactElement => {
    if (companies.status === FETCHING_STATUSES.ERROR) {
      return (
        <Reload
          onClick={handleGetCompanies}
          containerClassName={theme('reload')}
          iconClassName={theme('reload-icon')}
        />
      );
    }
    if (isEmpty(companies.data)) {
      if (companies.status === FETCHING_STATUSES.LOADING) {
        return <Spinner className={theme('spinner')} />;
      }
      return <Empty message={formatMessage(messages.empty)} />;
    }
    return (
      <>
        <div
          className={theme('table', { loading: FETCHING_STATUSES.LOADING === companies.status })}
        >
          <Table companies={companies.data} location={location} navigate={navigate} />
        </div>
        {get(pages, 'last.page', 1) > 1 && <Pagination {...pages} onClick={handlePageChange} />}
      </>
    );
  };

  return (
    <ContentLayout
      isForm={isForm}
      title={formatMessage(messages.title)}
      side={
        <div className={theme('filters', { disabled: isForm })}>
          <Filters location={location} navigate={navigate} />
        </div>
      }
      content={
        isForm ? (
          <NewCompany navigate={navigate} />
        ) : (
          <div className={theme('content')}>
            <Actions location={location} navigate={navigate} />
            {renderTable()}
          </div>
        )
      }
    />
  );
};

export default Companies;
